import React, { useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, FileSpreadsheet, CheckCircle2, AlertTriangle, Copy, Download } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function AdminImport() {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [skipDup, setSkipDup] = useState(true);
  const [result, setResult] = useState(null);

  const onSelect = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    setPreview(null);
    setResult(null);
  };

  const doPreview = async () => {
    if (!file) return;
    setLoading(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/admin/import/preview", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPreview(data);
      if (data.missing_columns?.length) toast.error(`Colonnes manquantes: ${data.missing_columns.join(", ")}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const doImport = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("skip_duplicates", String(skipDup));
      const { data } = await api.post("/admin/import/commit", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setResult(data);
      toast.success(`${data.imported} question(s) importée(s)`);
      setPreview(null);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const downloadTemplate = () => {
    const header = "question,reponse_1,reponse_2,reponse_3,bonne_reponse,categories,theme_source,tags,difficulte,explication,actif";
    const example = 'Quelle est la capitale de la France ?,Paris,Lyon,Marseille,1,CULTURE FOOT,geographie,facile;géographie,Facile,Paris est la capitale.,oui';
    const blob = new Blob([header + "\n" + example], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "modele_import_questions.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="mx-auto max-w-5xl space-y-6">
      <div>
        <h1 className="font-display text-3xl font-extrabold uppercase tracking-tight">Import CSV / Excel</h1>
        <p className="text-sm text-slate-500">Importez rapidement une grande quantité de questions depuis votre fichier.</p>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-5">
        <div className="mb-4 flex items-start gap-3 rounded-xl bg-slate-50 p-4 text-sm text-slate-600">
          <FileSpreadsheet className="mt-0.5 h-5 w-5 shrink-0 text-dh-red" />
          <div>
            <p className="font-semibold text-slate-900">Colonnes attendues</p>
            <p className="mt-1"><code className="rounded bg-white px-1">question</code>, <code className="rounded bg-white px-1">reponse_1..3</code>, <code className="rounded bg-white px-1">bonne_reponse</code> (1-3, lettre A/B/C ou texte exact), <code className="rounded bg-white px-1">categories</code>, <code className="rounded bg-white px-1">theme_source</code>, <code className="rounded bg-white px-1">tags</code>, <code className="rounded bg-white px-1">difficulte</code>, <code className="rounded bg-white px-1">explication</code>, <code className="rounded bg-white px-1">actif</code>. Catégories/tags multiples séparés par <b>;</b> ou <b>,</b></p>
            <button onClick={downloadTemplate} className="mt-2 inline-flex items-center gap-1.5 text-sm font-semibold text-dh-red"><Download className="h-4 w-4" /> Télécharger un modèle CSV</button>
          </div>
        </div>

        <div className="flex flex-col items-center justify-center rounded-2xl border-2 border-dashed border-slate-300 p-8">
          <input ref={fileRef} data-testid="import-file-input" type="file" accept=".csv,.xlsx,.xls" onChange={onSelect} className="hidden" />
          <Upload className="mb-3 h-10 w-10 text-slate-300" />
          {file ? (
            <p className="font-semibold text-slate-900">{file.name}</p>
          ) : (
            <p className="text-slate-400">Sélectionnez un fichier CSV ou Excel</p>
          )}
          <div className="mt-4 flex gap-3">
            <button data-testid="import-choose-btn" onClick={() => fileRef.current?.click()} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold">Choisir un fichier</button>
            <button data-testid="import-preview-btn" onClick={doPreview} disabled={!file || loading} className="rounded-xl bg-dh-red px-4 py-2.5 text-sm font-bold text-white disabled:opacity-40">{loading && !result ? "Analyse..." : "Aperçu"}</button>
          </div>
        </div>
      </div>

      {preview && (
        <div className="space-y-4 rounded-2xl border border-slate-200 bg-white p-5" data-testid="import-preview">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <StatBox label="Lignes" value={preview.total_rows} />
            <StatBox label="Valides" value={preview.valid_count} color="text-dh-green" />
            <StatBox label="Doublons" value={preview.duplicate_count} color="text-dh-gold" icon={<Copy className="h-4 w-4" />} />
            <StatBox label="Invalides" value={preview.invalid_count} color="text-dh-red" />
          </div>

          {preview.new_categories?.length > 0 && (
            <Info tone="blue">Nouvelles catégories qui seront créées : <b>{preview.new_categories.join(", ")}</b></Info>
          )}
          {preview.new_tags?.length > 0 && (
            <Info tone="blue">Tags détectés : {preview.new_tags.join(", ")}</Info>
          )}

          <div className="max-h-80 overflow-auto rounded-xl border border-slate-100">
            <table className="w-full text-left text-sm">
              <thead className="sticky top-0 bg-slate-50 text-xs uppercase text-slate-400">
                <tr>
                  <th className="p-2">Ligne</th><th className="p-2">Question</th><th className="p-2">Catégories</th><th className="p-2">État</th>
                </tr>
              </thead>
              <tbody>
                {preview.rows.map((r) => (
                  <tr key={r.line} className="border-t border-slate-100">
                    <td className="p-2 text-slate-400">{r.line}</td>
                    <td className="p-2">{r.text || <span className="text-slate-300">(vide)</span>}</td>
                    <td className="p-2 text-slate-500">{r.category_names.join(", ")}</td>
                    <td className="p-2">
                      {!r.valid ? <span className="rounded-full bg-dh-red/10 px-2 py-0.5 text-xs font-semibold text-dh-red">{r.errors.join("; ")}</span>
                        : r.duplicate ? <span className="rounded-full bg-dh-gold/10 px-2 py-0.5 text-xs font-semibold text-dh-gold">Doublon</span>
                        : <span className="rounded-full bg-dh-green/10 px-2 py-0.5 text-xs font-semibold text-dh-green">OK</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={skipDup} onChange={(e) => setSkipDup(e.target.checked)} data-testid="skip-dup-checkbox" /> Ignorer les doublons (questions au texte identique)
          </label>

          <button data-testid="import-commit-btn" onClick={doImport} disabled={loading || preview.valid_count === 0} className="flex h-12 w-full items-center justify-center gap-2 rounded-xl bg-dh-red font-display text-base font-extrabold uppercase tracking-wide text-white disabled:opacity-40">
            {loading ? "Import en cours..." : `Importer ${preview.valid_count} question(s)`}
          </button>
        </div>
      )}

      {result && (
        <div className="rounded-2xl border border-dh-green/40 bg-dh-green/5 p-5" data-testid="import-result">
          <div className="flex items-center gap-3">
            <CheckCircle2 className="h-8 w-8 text-dh-green" />
            <div>
              <p className="font-display text-xl font-extrabold text-slate-900">Import terminé</p>
              <p className="text-sm text-slate-600"><b>{result.imported}</b> importée(s) · {result.skipped} doublon(s) ignoré(s) · {result.error_count} erreur(s)</p>
            </div>
          </div>
          {result.errors?.length > 0 && (
            <div className="mt-3 space-y-1 text-sm text-dh-red">
              {result.errors.slice(0, 20).map((e, i) => <p key={i}><AlertTriangle className="mr-1 inline h-3.5 w-3.5" /> Ligne {e.line}: {e.errors.join("; ")}</p>)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function StatBox({ label, value, color = "text-slate-900", icon }) {
  return (
    <div className="rounded-xl border border-slate-100 bg-slate-50 p-3 text-center">
      <div className={`flex items-center justify-center gap-1 font-display text-2xl font-extrabold ${color}`}>{icon}{value}</div>
      <div className="text-xs font-semibold uppercase tracking-wider text-slate-400">{label}</div>
    </div>
  );
}

function Info({ tone = "blue", children }) {
  return <div className="rounded-xl border border-dh-blue/30 bg-dh-blue/5 p-3 text-sm text-slate-700">{children}</div>;
}
